import React, { useState, useEffect } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { 
  getUsageStats, 
  getUserSubscriptionTier, 
  getSubscriptionLimits,
  calculateUsagePercentage,
  detectMediaTypeFromMessage,
  isUnlimitedPlan
} from '../../utils/mediaUsageUtils';
import './UsageIndicator.css';

const UsageIndicator = ({ 
  position = "top-right", 
  show = true,
  onLimitExceeded,
  currentMessage = "" 
}) => {
  const { user } = useAuthenticator((context) => [context.user]);
  const [usageData, setUsageData] = useState(null);
  const [subscriptionTier, setSubscriptionTier] = useState("0");
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && show) {
      fetchUsageData();
    }
  }, [user, show]);

  const fetchUsageData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const [tier, stats] = await Promise.all([
        getUserSubscriptionTier(user),
        getUsageStats(user)
      ]);

      setSubscriptionTier(tier);
      if (stats.success) {
        setUsageData(stats.data);
      }
    } catch (error) {
      console.error('Error fetching usage data for indicator:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  if (!user || !show || loading) {
    return null;
  }

  if (!usageData) {
    return (
      <div className={`usage-indicator ${position}`}>
        <div className="indicator-icon error" title="Unable to load usage data">
          ⚠️
        </div>
      </div>
    );
  }

  const limits = getSubscriptionLimits(subscriptionTier);
  const pictureCount = usageData.picture_count || 0;
  const pictureLimit = limits.monthly_pictures;
  const pictureUnlimited = isUnlimitedPlan(pictureLimit);
  const pictureRemaining = pictureUnlimited ? "∞" : Math.max(0, pictureLimit - pictureCount);
  const picturePercentage = pictureUnlimited ? 0 : calculateUsagePercentage(pictureCount, pictureLimit);
  
  const videoCount = usageData.video_count || 0;
  const videoLimit = limits.monthly_videos;
  const videoUnlimited = isUnlimitedPlan(videoLimit);
  const videoRemaining = videoUnlimited ? "∞" : Math.max(0, videoLimit - videoCount);
  const videoPercentage = videoUnlimited ? 0 : calculateUsagePercentage(videoCount, videoLimit);

  // Detect if current message is requesting media
  const requestedMediaType = detectMediaTypeFromMessage(currentMessage);
  const isRequestingPicture = requestedMediaType === "picture";
  const isRequestingVideo = requestedMediaType === "video";
  
  // Determine warning state (no warnings for unlimited plans)
  const pictureWarning = isRequestingPicture && !pictureUnlimited && pictureRemaining === 0;
  const videoWarning = isRequestingVideo && !videoUnlimited && videoRemaining === 0;
  const anyWarning = pictureWarning || videoWarning;

  // Get status for display
  const getStatusIcon = () => {
    if (anyWarning) return "🚫";
    if (pictureUnlimited || videoUnlimited) return "∞";
    if (picturePercentage >= 80 || videoPercentage >= 80) return "⚠️";
    return "📊";
  };

  const getStatusColor = () => {
    if (anyWarning) return "error";
    if (picturePercentage >= 80 || videoPercentage >= 80) return "warning";
    return "normal";
  };

  const handleLimitExceededClick = () => {
    if (onLimitExceeded && anyWarning) {
      const exceededType = pictureWarning ? "picture" : "video";
      onLimitExceeded(exceededType, {
        current: exceededType === "picture" ? pictureCount : videoCount,
        limit: exceededType === "picture" ? pictureLimit : videoLimit,
        subscriptionTier
      });
    }
  };

  return (
    <div className={`usage-indicator ${position} ${expanded ? 'expanded' : ''}`}>
      <div 
        className={`indicator-main ${getStatusColor()}`}
        onClick={anyWarning ? handleLimitExceededClick : toggleExpanded}
        title={anyWarning ? 
          `Media limit reached! Click for upgrade options.` : 
          `Click to view usage details`
        }
      >
        <span className="indicator-icon">{getStatusIcon()}</span>
        {!expanded && (
          <span className="indicator-text">
            {anyWarning ? "Limit Reached" : 
             pictureUnlimited ? "Unlimited" : 
             `${pictureRemaining}/${pictureLimit}`}
          </span>
        )}
      </div>

      {expanded && !anyWarning && (
        <div className="indicator-details">
          <div className="details-header">
            <span className="plan-name">{limits.name}</span>
            <button 
              className="close-button"
              onClick={() => setExpanded(false)}
              title="Close"
            >
              ×
            </button>
          </div>
          
          <div className="usage-item">
            <div className="usage-row">
              <span className="media-icon">📸</span>
              <span className="media-label">Pictures</span>
              <span className="usage-count">
                {pictureUnlimited ? `${pictureCount}/∞` : `${pictureCount}/${pictureLimit}`}
              </span>
            </div>
            <div className="usage-bar">
              <div 
                className={`usage-fill ${pictureUnlimited ? 'unlimited' : picturePercentage >= 80 ? 'warning' : 'normal'}`}
                style={{ width: `${pictureUnlimited ? 100 : picturePercentage}%` }}
              ></div>
            </div>
          </div>

          {videoLimit > 0 && (
            <div className="usage-item">
              <div className="usage-row">
                <span className="media-icon">🎥</span>
                <span className="media-label">Videos</span>
                <span className="usage-count">
                  {videoUnlimited ? `${videoCount}/∞` : `${videoCount}/${videoLimit}`}
                </span>
              </div>
              <div className="usage-bar">
                <div 
                  className={`usage-fill ${videoUnlimited ? 'unlimited' : videoPercentage >= 80 ? 'warning' : 'normal'}`}
                  style={{ width: `${videoUnlimited ? 100 : videoPercentage}%` }}
                ></div>
              </div>
            </div>
          )}
          
          <div className="details-footer">
            <span className="refresh-text">Updates automatically</span>
          </div>
        </div>
      )}

      {anyWarning && (
        <div className="limit-warning">
          <div className="warning-header">
            <span className="warning-icon">🚫</span>
            <span className="warning-title">Limit Reached</span>
          </div>
          <div className="warning-message">
            You've used all your {pictureWarning ? "pictures" : "videos"} for this month
          </div>
          <button 
            className="upgrade-button-small"
            onClick={handleLimitExceededClick}
          >
            Upgrade Plan
          </button>
        </div>
      )}
    </div>
  );
};

export default UsageIndicator;